import FetchWrapper from './FetchWrapper'
import {
  API_BASE_URL,
  AUTH_BASE_URL,
  HEADERS,
  JSON_CONTENT,
} from '../configs/Constants'

// Enhanced error handling utility
const handleApiError = async (response, operation) => {
  if (!response.ok) {
    let errorMessage = `${operation} failed: ${response.status}`
    
    // Try to get more detailed error message from response
    try {
      const errorData = await response.text()
      if (errorData) {
        errorMessage += ` - ${errorData}`
      }
    } catch (parseError) {
      // If we can't parse the error, use the generic message
    }
    
    throw new Error(errorMessage)
  }
  return response
}

// Base request handler with common error handling
const createBaseRequest = (method) => {
  return async (url, data = null, additionalHeaders = {}) => {
    const wrapper = FetchWrapper.instance.addHeaders(HEADERS)
    
    // Add additional headers if provided
    Object.keys(additionalHeaders).forEach(key => {
      wrapper.addHeaders({ [key]: additionalHeaders[key] })
    })

    let response
    switch (method) {
      case 'GET':
        response = await wrapper.get(url, data || '')
        break
      case 'POST':
        response = await wrapper.postData(url, data)
        break
      case 'POST_WITH_CREDENTIALS':
        response = await wrapper.postWithCredentials(url, data)
        break
      default:
        throw new Error(`Unsupported method: ${method}`)
    }

    return response
  }
}

export const apiConfig = () => {
  // Create reusable request handlers
  const get = createBaseRequest('GET')
  const post = createBaseRequest('POST')
  const postWithCredentials = createBaseRequest('POST_WITH_CREDENTIALS')

  // Data fetching operations
  const fetchSimilarityLogs = async (reqId) => {
    if (!reqId) {
      throw new Error('Request ID is required')
    }
    
    const response = await get(`${API_BASE_URL}/data/similarity/logs/${reqId}`)
    await handleApiError(response, 'Fetch similarity logs')
    
    const data = await response.json()
    console.log('Fetched similarity logs:', data)
    return data
  }

  const getSimilarity = async (data) => {
    if (!data) {
      throw new Error('Data is required for similarity calculation')
    }
    
    console.log('Getting similarity for:', data)
    const response = await post(`${API_BASE_URL}/similarity`, data, JSON_CONTENT)
    await handleApiError(response, 'Get similarity')
    
    return (await response.json()) || []
  }

  const postDtp = async (data) => {
    if (!data) {
      throw new Error('Data is required for DTP posting')
    }
    
    const response = await FetchWrapper.instance
      .addHeaders(HEADERS)
      .post(`${API_BASE_URL}/ligtmap`, data)
    
    await handleApiError(response, 'Post DTP')
    return await response.json()
  }

  const getDtp = async (smile, reqId) => {
    if (!smile) {
      throw new Error('SMILE string is required')
    }
    
    const encodedSmiles = encodeURIComponent(smile)
    let url = `${API_BASE_URL}/data/dtp/${encodedSmiles}`
    if (reqId) {
      url += `?request_id=${reqId}`
    }

    const response = await get(url)
    await handleApiError(response, 'Get DTP')
    
    return await response.json()
  }

  const getHmdbData = async (uniprot) => {
    if (!uniprot) {
      throw new Error('UniProt ID is required')
    }
    
    const response = await get(`${API_BASE_URL}/data/hmdb/${uniprot}`)
    await handleApiError(response, 'Get HMDB data')
    
    return await response.json()
  }

  const getHmdbDataBySmile = async (smile) => {
    if (!smile) {
      throw new Error('SMILE string is required')
    }
    
    const response = await get(`${API_BASE_URL}/data/hmdb/smile/${smile}`)
    await handleApiError(response, 'Get HMDB data by SMILE')
    
    return await response.json()
  }

  const getDock = async (smile, pdb, reqId) => {
    if (!smile && !pdb) {
      throw new Error('Either SMILE string or PDB ID is required')
    }
    
    let url = `${API_BASE_URL}/data/docking/search/`
    if (smile && pdb) {
      url += `ligand/${smile}/target/${pdb}`
    } else if (smile) {
      url += `ligand/${smile}`
    } else if (pdb) {
      url += `target/${pdb}`
    }

    if (reqId) {
      url += `?request_id=${reqId}`
    }
    
    const response = await get(url)
    await handleApiError(response, 'Get docking data')
    
    return await response.json()
  }

  // User management operations
  const getUserByUserId = async (userId) => {
    if (!userId) {
      throw new Error('User ID is required')
    }
    
    const response = await FetchWrapper.instance.get(
      `${API_BASE_URL}/user/fetch/${userId}`,
      '',
    )
    
    await handleApiError(response, 'Get user by ID')
    
    if (response.status === 204) {
      return null
    }
    return new Response(response.body)
  }

  const getUserDetailById = async (userId) => {
    if (!userId) {
      throw new Error('User ID is required')
    }
    
    const response = await FetchWrapper.instance.get(
      `${API_BASE_URL}/user/fetchDetail/${userId}`,
      '',
    )
    
    await handleApiError(response, 'Get user details by ID')
    
    if (response.status === 204) {
      return null
    }
    return response.json()
  }

  const getUserByEmail = async (email) => {
    if (!email || !email.includes('@')) {
      throw new Error('Valid email address is required')
    }
    
    const response = await FetchWrapper.instance.get(
      `${API_BASE_URL}/user/find/${email}`,
      '',
    )
    
    await handleApiError(response, 'Get user by email')
    
    if (response.status === 204) {
      return null
    }
    return new Response(response.body)
  }

  // Authentication operations
  const createUser = async (user) => {
    const response = await postWithCredentials(
      `${AUTH_BASE_URL}/user/create`, 
      user, 
      JSON_CONTENT
    )
    
    await handleApiError(response, 'Create user')
    return response
  }

  const updateProfile = async (user) => {
    if (!user) {
      throw new Error('User data is required')
    }
    
    const response = await postWithCredentials(
      `${AUTH_BASE_URL}/user/update`, 
      user, 
      JSON_CONTENT
    )
    
    await handleApiError(response, 'Update profile')
    return response
  }

  const confirmSignUp = async (token, userId, isSignUp = true) => {
    if (!token || !userId) {
      throw new Error('Token and User ID are required')
    }
    
    const response = await postWithCredentials(
      `${AUTH_BASE_URL}/user/confirm/${token}/${userId}?isSignUp=${isSignUp}`,
      null,
      JSON_CONTENT
    )
    
    await handleApiError(response, 'Confirm sign up')
    return response.text()
  }

  const loginUser = async (user) => {
    const response = await postWithCredentials(
      `${AUTH_BASE_URL}/user/login`, 
      user, 
      JSON_CONTENT
    )

    if (response.ok) {
      return (await response.text()) || []
    } else if (response.status === 403) {
      const errorText = await response.text()
      throw new Error(errorText || 'Authentication failed')
    } else {
      throw new Error(`Login failed: ${response.status}`)
    }
  }

  const validateAndFetchDeviceQR = async (user) => {
    if (!user) {
      throw new Error('User data is required')
    }
    
    const response = await postWithCredentials(
      `${AUTH_BASE_URL}/user/validateAndFetchDeviceQR`, 
      user, 
      JSON_CONTENT
    )
    
    if (response.ok) {
      return (await response.json()) || []
    } else {
      const errorData = await response.json()
      throw new Error(errorData.errors || `Device QR validation failed: ${response.status}`)
    }
  }

  const getPublicKey = async (userId) => {
    if (!userId) {
      throw new Error('User ID is required')
    }
    
    console.log('Fetching public key for user:', userId)
    const response = await FetchWrapper.instance.get(
      `${AUTH_BASE_URL}/user/key/${userId}`,
      ''
    )
    
    await handleApiError(response, 'Get public key')
    return (await response.text()) || []
  }

  const validateLogin = async (authHeader) => {
    if (!authHeader) {
      throw new Error('Authorization header is required')
    }
    
    const response = await FetchWrapper.instance
      .addHeaders(HEADERS)
      .addHeaders(authHeader)
      .get(`${AUTH_BASE_URL}/user/login`, '')
    
    await handleApiError(response, 'Validate login')
    return await response.json()
  }

  const verifySessionToken = async () => {
    const response = await postWithCredentials(`${API_BASE_URL}/user/validate/sessionToken`)
    await handleApiError(response, 'Verify session token')
    return await response.json()
  }

  const logoutSession = async () => {
    const response = await postWithCredentials(`${API_BASE_URL}/user/session/logout`)
    await handleApiError(response, 'Logout session')
    return await response.json()
  }

  // Chemical analysis operations
  const getAdmet = async (data) => {
    if (!data) {
      throw new Error('Data is required for ADMET analysis')
    }
    
    const response = await post(`${API_BASE_URL}/data/admet`, data, JSON_CONTENT)
    await handleApiError(response, 'Get ADMET data')
    
    return await response.json()
  }

  const verifyEmailActivationToken = async (activationToken, resend = false) => {
    if (!activationToken) {
      throw new Error('Activation token is required')
    }
    
    const response = await FetchWrapper.instance
      .addHeaders(HEADERS)
      .postData(`${API_BASE_URL}/user/verify-email/${activationToken}/${resend}`)

    await handleApiError(response, 'Verify email activation token')
    return (await response.text()) || []
  }

  // Enhanced solubility calculation methods
  const calculateWaterSolubility = async (smiles) => {
    if (!smiles) {
      throw new Error('SMILES string is required for water solubility calculation')
    }
    
    try {
      const response = await post(
        `${API_BASE_URL}/admet/predict`, 
        JSON.stringify(smiles),
        JSON_CONTENT
      )
      
      await handleApiError(response, 'Calculate water solubility')
      
      const data = await response.json()
      if (!data) {
        throw new Error('Invalid response format from water solubility calculation')
      }
      
      return data
    } catch (error) {
      console.error('Water solubility calculation error:', error)
      throw error
    }
  }

  const calculateSolventSoluteSolubility = async (soluteSmiles, solventSmiles, temperature) => {
    if (!soluteSmiles || !solventSmiles) {
      throw new Error('Both solute and solvent SMILES strings are required')
    }
    if (temperature === null || temperature === undefined) {
      throw new Error('Temperature is required')
    }
    
    const requestData = {
      solute_smiles: soluteSmiles,
      solvent_smiles: solventSmiles,
      temperature: temperature
    }
    
    const response = await post(
      `${API_BASE_URL}/admet/solubility/solv_solu_predict`,
      JSON.stringify(requestData),
      JSON_CONTENT
    )
    
    await handleApiError(response, 'Calculate solvent-solute solubility')
    return await response.json()
  }

  const calculateSolubilityWithThermodynamics = async (formData) => {
    if (!formData) {
      throw new Error('Form data is required for thermodynamic solubility calculation')
    }
    
    const requiredFields = ['soluteSmiles', 'solventSmiles', 'temperature']
    const missingFields = requiredFields.filter(field => !formData[field])
    
    if (missingFields.length > 0) {
      throw new Error(`Missing required fields: ${missingFields.join(', ')}`)
    }
    
    const requestData = {
      solute_smiles: formData.soluteSmiles,
      solvent_smiles: formData.solventSmiles,
      temperature: formData.temperature,
      ph: formData.ph,
      delta_H_sol: formData.deltaHsol,
      pKa: formData.pKa,
      S_pure: formData.SPure,
      molecular_weight: formData.molecularWeight
    }
    
    const response = await post(
      `${API_BASE_URL}/admet/calculate`,
      JSON.stringify(requestData),
      JSON_CONTENT
    )
    
    await handleApiError(response, 'Calculate thermodynamic solubility')
    return await response.json()
  }

  const fetchDrugex = async (data) => {
    if (!data) {
      throw new Error('Data is required for Drug Excipient Compatibility prediction')
    }
    
    const response = await post(
      `${API_BASE_URL}/drugex/predict`,
      JSON.stringify(data),
      JSON_CONTENT
    )
    
    await handleApiError(response, 'Fetch Drug Excipient Compatibility')
    return await response.json()
  }

  const executeDossier = async (data) => {
    if (!data) {
      throw new Error('Data is required for dossier execution')
    }

    const response = await post(
      `${API_BASE_URL}/dossier/execute`,
      JSON.stringify(data),
      JSON_CONTENT
    )

    await handleApiError(response, 'Execute dossier')
    return await response.json()
  }

  const getDossierStatus = async (jobId) => {
    if (!jobId) {
      throw new Error('Job ID is required')
    }

    const response = await get(`${API_BASE_URL}/dossier/status/${jobId}`)
    await handleApiError(response, 'Get dossier status')

    return await response.json()
  }

  const getDossiersByUserId = async (userId) => {
    if (!userId) {
      throw new Error('User ID is required')
    }

    const response = await get(`${API_BASE_URL}/data/dossier/${userId}`)
    await handleApiError(response, 'Get dossiers by user ID')

    return await response.json()
  }

  const analyzeStructureApi = async (content, format = 'pdb') => {
    if (!content) {
      throw new Error('Structure content is required')
    }
    try {
      const response = await post(
        `${API_BASE_URL}/api/structure/analyze`,
        JSON.stringify({ content, format }),
        JSON_CONTENT
      )
      if (response && response.ok) {
        return await response.json()
      }
    } catch (error) {
      console.warn('Server structure analysis failed or endpoint offline:', error)
    }
    return null
  }

  const logUserEvent = async (eventData) => {
    if (!eventData) {
      throw new Error('Event data is required')
    }

    // We do not await or throw error for logging to not interrupt user flow
    // but we catch errors to log them to console
    try {
      await post(
        `${API_BASE_URL}/data/event`,
        JSON.stringify(eventData),
        JSON_CONTENT
      )
    } catch (error) {
      console.error('Failed to log event:', error)
    }
  }

  // Return all API methods (maintaining exact same interface)
  return {
    // Dossier operations
    executeDossier,
    getDossierStatus,
    getDossiersByUserId,

    // Data operations
    getSimilarity,
    fetchSimilarityLogs,
    getAdmet,
    
    // DTP operations
    getDtp,
    postDtp,
    
    // HMDB operations
    getHmdbData,
    getHmdbDataBySmile,
    
    // Docking operations
    getDock,
    
    // User management
    getUserByUserId,
    getUserDetailById,
    getUserByEmail,
    createUser,
    updateProfile,
    
    // Authentication
    loginUser,
    validateLogin,
    verifySessionToken,
    logoutSession,
    validateAndFetchDeviceQR,
    
    // Email verification
    verifyEmailActivationToken,
    confirmSignUp,
    
    // Cryptography
    getPublicKey,
    
    // Solubility calculations
    calculateWaterSolubility,
    calculateSolventSoluteSolubility,
    calculateSolubilityWithThermodynamics,
    
    // Drug analysis
    fetchDrugex,

    // Analytics
    analyzeStructureApi,
    logUserEvent
  }
}
